import { z } from "zod";
import { IsoDateTimeSchema } from "./common.js";

export const MemoryFileIdSchema = z.enum(["soul", "user", "memory"]);
export const MemoryRevisionSchema = z.string().regex(/^memrev_[a-f0-9]{24}$/u);
export const MemoryBackupNameSchema = z
  .string()
  .regex(/^(?:SOUL|USER|MEMORY)\.md\.bak\.\d{1,20}$/u);

export const MemoryDocumentSchema = z
  .object({
    fileId: MemoryFileIdSchema,
    displayName: z.enum(["SOUL.md", "USER.md", "MEMORY.md"]),
    logicalPath: z.enum(["/SOUL.md", "/memories/USER.md", "/memories/MEMORY.md"]),
    content: z.string().max(256 * 1024),
    revision: MemoryRevisionSchema,
    sizeBytes: z
      .number()
      .int()
      .min(0)
      .max(256 * 1024),
    charCount: z
      .number()
      .int()
      .min(0)
      .max(256 * 1024),
    charLimit: z
      .number()
      .int()
      .positive()
      .max(256 * 1024)
      .nullable(),
    overLimit: z.boolean(),
    kind: z.enum(["identity", "builtin_memory"]),
    writeMechanism: z.enum(["hermes_profile", "hermes_memory_tool"]),
    lineEnding: z.enum(["lf", "crlf", "mixed", "none"]),
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const MemoryBackupSummarySchema = z
  .object({
    filename: MemoryBackupNameSchema,
    updatedAt: IsoDateTimeSchema,
  })
  .strict();

export const MemoryHistorySchema = z
  .object({
    fileId: MemoryFileIdSchema,
    backups: z.array(MemoryBackupSummarySchema).max(100),
  })
  .strict();

export const MemoryBackupDetailSchema = z
  .object({
    fileId: MemoryFileIdSchema,
    backup: MemoryBackupSummarySchema,
    content: z.string().max(256 * 1024),
  })
  .strict();

export const SaveMemoryDocumentRequestSchema = z
  .object({
    content: z.string().max(256 * 1024),
    expectedRevision: MemoryRevisionSchema,
  })
  .strict();

export const SaveMemoryDocumentResponseSchema = z
  .object({
    document: MemoryDocumentSchema,
    idempotent: z.boolean(),
    mechanism: z.enum(["hermes_profile", "hermes_memory_tool"]),
  })
  .strict();

export type MemoryFileId = z.infer<typeof MemoryFileIdSchema>;
export type MemoryRevision = z.infer<typeof MemoryRevisionSchema>;
export type MemoryBackupName = z.infer<typeof MemoryBackupNameSchema>;
export type MemoryDocument = z.infer<typeof MemoryDocumentSchema>;
export type MemoryBackupSummary = z.infer<typeof MemoryBackupSummarySchema>;
export type MemoryHistory = z.infer<typeof MemoryHistorySchema>;
export type MemoryBackupDetail = z.infer<typeof MemoryBackupDetailSchema>;
export type SaveMemoryDocumentRequest = z.infer<typeof SaveMemoryDocumentRequestSchema>;
export type SaveMemoryDocumentResponse = z.infer<typeof SaveMemoryDocumentResponseSchema>;
